import React from "react";
import { PATHS } from "./paths";
import { getRouteCandidates, type SystemMenuNode } from "./systemMenu";
import { handleConsultOpenedBoxes } from "@/pages/Caixa/ConsultarCaixasAbertas/service";

export type MenuOpenMode = "route" | "tab";

// Modo usado quando o nó não declara `mode`. O GED hoje abre tudo por aba — "tab" mantém esse
// comportamento como default.
export const DEFAULT_OPEN_MODE: MenuOpenMode = "tab";

export interface MenuNode {
  label: string;
  icon?: React.ComponentType;
  /** Present only on leaf nodes (no children) — navigable route path without leading slash */
  path?: string;
  /** Present only on parent nodes (no path) — expandable group */
  children?: MenuNode[];
  /** Sobrescreve DEFAULT_OPEN_MODE nesta folha */
  mode?: MenuOpenMode;
  action?: (params: unknown) => Promise<void>;
  /** Só em folha vinda do backend (via buildMenuTree) — código de operação (_OP) pedido */
  initialAction?: string;
}

// Nó fixo, fora do fluxo dinâmico do backend — não vem do request 116, sempre visível.
export const DASHBOARD_NODE: MenuNode = {
  label: "Início",
  path: PATHS.DASHBOARD,
  mode: "route"
};

// Itens do menu dinâmico com comportamento além de "abrir a tela" (mesmo path de sempre, ação
// custom em vez de navegação/aba) — preserva o único uso real disso hoje no GED
// (handleConsultOpenedBoxes, "Consultar Caixas Abertas"), que não tem como vir do backend (o
// request 116 só carrega dado de tela, não referências de código do frontend).
const PATH_ACTION_MAP: Record<string, (params: unknown) => Promise<void>> = {
  [PATHS.CAIXA.CONSULTAR_CAIXAS_ABERTAS]: handleConsultOpenedBoxes as (params: unknown) => Promise<void>
};

// `_UC:_OP` (ver systemMenu.ts) -> path de rota já implementado no GED. Cada tela CRUD nova entra
// aqui — sem entrada, o MI/Submenu correspondente do menu do backend é ignorado (comportamento
// desejado: tela ainda não portada pro React simplesmente não aparece, sem quebrar os irmãos).
//
// Levantado ao vivo em 2026-08-27 (Fase 0 do plano) a partir da resposta real do request 116 — cobre
// as ~24 telas de negócio de `routes.ts` (as rotas `DOCUMENTS.*`/`SETTINGS.*` são scaffold do
// template, não têm UC real no GED, ficam de fora de propósito). `_UC`/`_OP` cross-checados contra
// `gucManager.RegisterClass` de cada UC em `srv/src/ucu/` durante a auditoria da Fase 7 — todos
// batem. Ver harness/29-refactor-implementar-menu-de-acordo-com-permissoes/plans/
// 2026-08-27-menu-dinamico-e-permissoes.md, seção "Fase 0", pra tabela completa e a resposta bruta.
export const ROUTE_MAP: Record<string, string> = {
  "2559:1": PATHS.CADASTRO.EMPRESA, // Cadastro > Empresa
  "3393:5": PATHS.CADASTRO.PERMISSAO_REGISTRO, // Cadastro > Permissão de Registro > Registrar Permissão do Usuário
  "2539:1": PATHS.CADASTRO.TIPO_DOCUMENTO.INCLUIR, // Cadastro > Tipo documento > Incluir
  "2539:3": PATHS.CADASTRO.TIPO_DOCUMENTO.ALTERAR, // Cadastro > Tipo documento > Alterar
  "2539:4": PATHS.CADASTRO.TIPO_DOCUMENTO.EXCLUIR, // Cadastro > Tipo documento > Excluir
  "2708:5": PATHS.CADASTRO.TIPO_DOCUMENTO.MOVER_DOCUMENTOS, // Cadastro > Tipo documento > Mover Documentos
  "2556:2": PATHS.CAIXA.CONSULTAR_DOCUMENTOS, // Caixa > Consultar Documentos
  "2558:2": PATHS.CAIXA.CONSULTAR_HISTORIA, // Caixa > Consultar História
  "2702:2": PATHS.CAIXA.CONSULTAR_CAIXAS_ABERTAS, // Caixa > Consultar Caixas Abertas
  "3607:5": PATHS.CAIXA.MOVIMENTAR_DOCUMENTOS, // Caixa > Movimentar Documentos
  "2540:1": PATHS.DOCUMENTOS.CADASTRO.INCLUIR, // Documentos > Cadastro > Incluir
  "2540:3": PATHS.DOCUMENTOS.CADASTRO.ALTERAR, // Documentos > Cadastro > Alterar
  "2540:4": PATHS.DOCUMENTOS.CADASTRO.EXCLUIR, // Documentos > Cadastro > Excluir
  "2551:5": PATHS.DOCUMENTOS.LOCALIZAR.LOCALIZAR_POR_TIPO_DOCUMENTO, // Documentos > Localizar > Localizar Por Tipo de Documento
  "3071:2": PATHS.DOCUMENTOS.LOCALIZAR.LOCALIZAR_POR_CHAVE, // Documentos > Localizar > Localizar Por Chave
  "2593:1": PATHS.DOCUMENTOS.EMPRESTIMO.REALIZAR, // Documentos > Empréstimo > Realizar Empréstimo
  "2593:5": PATHS.DOCUMENTOS.EMPRESTIMO.REALIZAR_DEVOLUCAO, // Documentos > Empréstimo > Realizar Devolução
  // "Alterar" empacota 3 Actions (Alterar Empréstimo/Alterar Devolução/Desfazer Devolução) sob o mesmo
  // clique de menu — Alterar Empréstimo (_OP=3) e Desfazer Devolução (_OP=3) chegam a compartilhar
  // `_UC:_OP` (2593:3) apesar de `_UI` diferente entre si; as 3 abrem a MESMA tela React (ALTERAR),
  // que hoje não distingue submodo nenhum — ver getRouteCandidates (systemMenu.ts).
  "2593:3": PATHS.DOCUMENTOS.EMPRESTIMO.ALTERAR, // Documentos > Empréstimo > Alterar > Alterar Empréstimo | Desfazer Devolução
  "2593:6": PATHS.DOCUMENTOS.EMPRESTIMO.ALTERAR, // Documentos > Empréstimo > Alterar > Alterar Devolução
  // Fase 9: Excluir Empréstimo foi mesclado na tela de Alterar (mesmo path) — "Excluir" nunca aparece
  // como item de menu separado (decisão do usuário); o dedup por path abaixo garante que só "Alterar"
  // sobrevive quando as duas Actions resolvem para o mesmo usuário (ordem de declaração do backend).
  "2593:4": PATHS.DOCUMENTOS.EMPRESTIMO.ALTERAR, // Documentos > Empréstimo > Alterar > Excluir
  "2544:1": PATHS.EXPURGO.NOVO_LOTE, // Expurgo > Novo Lote para Expurgo
  "2549:5": PATHS.EXPURGO.EXPURGAR_CANCELAR_LOTE, // Expurgo > Expurgar/Cancelar Lote para Expurgo
  "2544:2": PATHS.EXPURGO.TERMO_EXPURGO, // Expurgo > Termo de Expurgo
  "2979:5": PATHS.EXPURGO.RELATORIO_EXPIRACAO_DOCUMENTOS // Expurgo > Relatório Expiração de Documentos
};

// Um item de Submenus é GRUPO ANINHADO quando ele mesmo tem Submenus; senão é FOLHA
// (Incluir/Consultar/Alterar/Excluir, MU direto ou MS->Actions).
const isNestedGroup = (node: SystemMenuNode): boolean => (node.Submenus?.length ?? 0) > 0;

// Resolve a folha tentando cada candidata de rota do nó em ordem (ver getRouteCandidates) — a
// primeira que tiver entrada em ROUTE_MAP vence. null se nenhuma candidata resolver.
const resolveLeaf = (node: SystemMenuNode): MenuNode | null => {
  for (const candidate of getRouteCandidates(node)) {
    const path = ROUTE_MAP[candidate.key];
    if (path) return { label: node._Name, path, initialAction: candidate.op, action: PATH_ACTION_MAP[path] };
  }
  return null;
};

// Dedup genérico por path: dois irmãos que resolvem para a MESMA rota (ex. "Alterar"/"Excluir" de
// Empréstimo, ambos mapeados em ROUTE_MAP pro path de ALTERAR desde a Fase 9) — mantém só o primeiro
// na ordem de declaração do backend, descarta o resto. Não mexe em grupos (sem `path`) nem em folhas
// com path distinto — serve pra qualquer merge futuro parecido, não só Empréstimo.
const pushDeduped = (acc: MenuNode[], node: MenuNode): void => {
  if (node.path && acc.some((existing) => existing.path === node.path)) return;
  acc.push(node);
};

const buildNode = (node: SystemMenuNode): MenuNode | null => {
  if (isNestedGroup(node)) {
    const children = node.Submenus!.reduce<MenuNode[]>((acc, sub) => {
      if (isNestedGroup(sub)) {
        const nested = buildNode(sub); // grupo aninhado — recursivo
        if (nested) pushDeduped(acc, nested);
        return acc;
      }
      const leaf = resolveLeaf(sub);
      if (leaf) pushDeduped(acc, leaf);
      return acc;
    }, []);
    // grupo sem NENHUM filho resolvido (todas as telas ainda não implementadas/mapeadas) — descarta
    // o grupo inteiro, não "achata" pra tela de um neto qualquer (ver nota na spec desta feature).
    return children.length > 0 ? { label: node._Name, children } : null;
  }

  return resolveLeaf(node);
};

export const buildMenuTree = (menus: SystemMenuNode[]): MenuNode[] =>
  menus.reduce<MenuNode[]>((acc, mi) => {
    const built = buildNode(mi);
    if (built) pushDeduped(acc, built);
    return acc;
  }, []);

export const flattenMenuPaths = (nodes: MenuNode[]): Set<string> => {
  const paths = new Set<string>();
  const visit = (list: MenuNode[]) => {
    for (const node of list) {
      if (node.path) paths.add(node.path);
      if (node.children) visit(node.children);
    }
  };
  visit(nodes);
  return paths;
};
